import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import useI18n from '@i18n';
import BaseGrid from '../../../../base/BaseGrid';

type StepperTextPosition = 'bottom' | 'right';
type StepperType = 'narrow' | 'wide';

export interface IStepperStep {
    img: string;
    caption: React.ReactNode;
}

interface IStepperProps {
    steps: IStepperStep[];
    textPosition?: StepperTextPosition;
    withArrows?: boolean;
    type: StepperType;
}

const Stepper: React.FC<IStepperProps> = ({
    steps,
    textPosition = 'bottom',
    withArrows = false,
    type,
}) => {
    const { t } = useI18n('Faq');
    let mdColSize = 12;
    let xlColSize = 12;
    let widthClasses = '';

    // в зависимости от type и textPosition меняются настройки колонок
    // type narrow – если узкие скрины (как на странице faq/self-employed)
    // type wide – если широкие скрины (как на странице info/telegram-bot)
    if (type === 'narrow') {
        mdColSize = textPosition === 'right' ? 6 : 4;
        xlColSize = textPosition === 'right' ? 6 : 3;
        widthClasses = textPosition === 'right' ? 'md:w-1/2' : '';
    } else {
        widthClasses = 'xl:w-1/2';
    }

    const figureClass =
        textPosition === 'bottom'
            ? 'flex flex-col mb-6 sm:mb-10'
            : 'items-center text-center flex flex-col mb-10 md:flex-row md:text-left';
    const figCaptionClass =
        textPosition === 'bottom'
            ? 'mt-4 text-center text-blueGray-500'
            : `ml-5 ${widthClasses} text-blueGray-500`;

    const renderSteps = (stepList: IStepperStep[]) => {
        return stepList.map(({ img, caption }, index) => {
            const isLastStep = index === steps.length - 1;
            return (
                <BaseGrid
                    item
                    initial={12}
                    xs={type === 'narrow' ? 6 : 12}
                    md={mdColSize}
                    xl={xlColSize}
                    key={`${img} ${caption}`}
                    className="pr-[16px] pl-[16px]"
                >
                    <figure className={figureClass}>
                        <div className={`relative ${widthClasses} lg:flex lg:items-center`}>
                            <img
                                className="border border-solid border-blueGray-200"
                                alt={t('Step', { step: index + 1 })}
                                src={img}
                                height="300"
                            />
                            {!isLastStep && withArrows && (
                                <span
                                    className="absolute hidden text-blueGray-300 top-1/2 text-h1 md:block lg:text-promo2 xl:static xl:ml-1.5 -translate-y-1/2 xl:translate-y-0"
                                    style={{ right: '-0.8em' }}
                                >
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </span>
                            )}
                        </div>
                        <figcaption className={figCaptionClass}>{caption}</figcaption>
                    </figure>
                </BaseGrid>
            );
        });
    };

    return (
        <BaseGrid container className="flex-wrap sm:mb-6">
            {renderSteps(steps)}
        </BaseGrid>
    );
};

export default Stepper;
